/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Controller } from 'react-hook-form';
import { Skeleton, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  FormDialog,
  FormDialogActions,
  FormDialogContent,
  FormDialogTitle,
  FormElement,
  FormInputsStack,
  FormSubmissionError,
  FormCancelButton,
  getFieldProps,
  Inputs,
  InputPhone,
  InputSkeleton,
  TimezoneSelector,
  ChipOption,
  getSubmitButtonProps,
} from '@marageti/z4-lib';
import { RoleName } from '@marageti/z4-sdk/lib/auth';
import { ViewProps } from './types';
import { validatePhone } from '../../../utils/helpers/validation';

const roleOptions: ChipOption[] = [
  { key: RoleName.AGENT_ADMIN, label: 'Manager / Admin', value: RoleName.AGENT_ADMIN },
  { key: RoleName.AGENT, label: 'Specialist', value: RoleName.AGENT },
  { key: RoleName.AGENT_SUPPORT, label: 'Support', value: RoleName.AGENT_SUPPORT },
];

const EditAgentFormView = ({
  control,
  defaultSelectedRoles,
  errors,
  handleSubmit,
  isSubmitting,
  isValid,
  onCancel,
  onSubmit,
  open,
  register,
  submissionError,
  agent,
}: ViewProps) => (
  <FormDialog
    open={open}
  >
    <FormDialogTitle handleClose={onCancel}>
      {agent?.fullName
        ? `Edit ${agent?.fullName}`
        : <Skeleton variant="rounded" />}
    </FormDialogTitle>
    <FormElement name="profile-editor-form" onSubmit={handleSubmit(onSubmit)}>
      <FormDialogContent>
        {!agent ? (
          <FormInputsStack>
            <InputSkeleton key="givenName" />
            <InputSkeleton key="familyName" />
            <InputSkeleton key="title" />
            <InputSkeleton key="role" />
            <InputSkeleton key="phone" />
            <InputSkeleton key="timeZone" />
          </FormInputsStack>
        ) : (
          <FormInputsStack>
            {submissionError
                && (
                  <FormSubmissionError
                    message={submissionError.message}
                    errorCode={submissionError.code}
                    errorId={submissionError.id}
                  />
                )}
            <Inputs.Text
              defaultValue={agent?.givenName}
              error={!!errors?.givenName}
              helperText={errors?.givenName?.message as string}
              id="givenName"
              label="First Name"
              required
              {...register('givenName', { required: 'Please enter a first name' })}
            />
            <Inputs.Text
              defaultValue={agent?.familyName}
              error={!!errors?.familyName}
              helperText={errors?.familyName?.message as string}
              id="familyName"
              label="Last Name"
              required
              {...register('familyName', { required: 'Please enter a last name' })}
            />
            <Inputs.Text
              defaultValue={agent?.title}
              error={!!errors?.title}
              helperText={errors?.title?.message as string}
              id="title"
              label="Title"
              {...register('title')}
            />
            <TextField
              select
              SelectProps={{
                native: true,
              }}
              id="role"
              label="Role"
              required
              defaultValue={defaultSelectedRoles}
              {...register('role')}
            >
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <option value="" />
              {roleOptions.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={option.value === RoleName.AGENT_SUPPORT}
                >
                  {option.label}
                </option>
              ))}
            </TextField>
            <Controller
              control={control}
              defaultValue={agent?.phone}
              name="phone"
              render={({ field }) => (
                <InputPhone
                  error={!!errors.phone}
                  id="phone"
                  helperText={errors.phone ? 'Please enter a valid number.' : ''}
                  label="Phone"
                  {...field}
                />
              )}
              rules={{ validate: validatePhone }}
            />
            <Controller
              control={control}
              defaultValue={agent?.timeZone}
              name="timeZone"
              render={({ field }) => (
                <TimezoneSelector
                  id="timeZone"
                  label="Timezone"
                  {...getFieldProps('timeZone', errors)}
                  {...field}
                />
              )}
            />
          </FormInputsStack>
        )}
      </FormDialogContent>
      <FormDialogActions>
        <FormCancelButton onClick={onCancel} />
        <LoadingButton
          variant="contained"
          type="submit"
          {...getSubmitButtonProps(isSubmitting, isValid)}
        >
          Submit
        </LoadingButton>
      </FormDialogActions>
    </FormElement>
  </FormDialog>
);

export default EditAgentFormView;
